import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DataIntegrityError, DatabaseError } from "../auth/errors";
import { SupportTicketMerchantTopicId } from "../ids/definitions";

export const TopicResponse = Schema.Struct({
  id: SupportTicketMerchantTopicId,
  name: Schema.String,
  description: Schema.String,
});
export type TopicResponse = typeof TopicResponse.Type;

const ListTopicsResponse = Schema.Struct({
  topics: Schema.Array(TopicResponse),
});

const CreateTopicInput = Schema.Struct({
  name: Schema.String,
  description: Schema.String,
});
const CreateTopicResponse = Schema.Struct({ id: SupportTicketMerchantTopicId });

const UpdateTopicInput = Schema.Struct({
  name: Schema.optional(Schema.String),
  description: Schema.optional(Schema.String),
});

/**
 * Per-account toggle for how custom topics get matched against tickets:
 * - "embedding" (default) — embed the ticket text and run a CRDB nearest-
 *   neighbor against `support_ticket_merchant_topic.embedding`. Cheap, deterministic.
 * - "agent" — call an LLM that takes the ticket transcript + the topic
 *   catalog (name + description per topic) and returns matched ids. More
 *   accurate on nuanced descriptions, but costs an LLM call per
 *   regeneration. The cost is recorded against the triggering message in
 *   `conversation_expense`.
 *
 * Surfaced in the merchant app behind a Redo-employees-only toggle.
 */
export const CustomTopicClassifierMode = Schema.Literal("embedding", "agent");
export type CustomTopicClassifierMode = typeof CustomTopicClassifierMode.Type;

/**
 * Lets a merchant type sample customer messages and see which of their
 * already-saved topics would be picked. Runs the same classifier the
 * runtime regenerator uses (embedding or agent, whichever the account is
 * configured for), so the displayed verdict predicts actual rule-eval
 * behavior.
 */
const TestTopicMatchesInput = Schema.Struct({
  testInputs: Schema.Array(Schema.String),
});

export const TestTopicMatch = Schema.Struct({
  topicId: SupportTicketMerchantTopicId,
  topicName: Schema.String,
  /**
   * Raw L2 distance from the test input to the topic's embedding. Null in
   * agent mode (the LLM makes binary match decisions and doesn't surface a
   * distance metric). Smaller is closer; ~2 is anti-correlated.
   */
  distance: Schema.NullOr(Schema.Number),
  /**
   * True when the runtime regenerator would tag a ticket carrying this
   * test input with this topic.
   *
   * - **embedding mode**: distance is below the match threshold.
   * - **agent mode**: the LLM listed this topic among the matches.
   *
   * In embedding mode a `matched: false` entry surfaces the closest
   * candidate to help merchants tune topic descriptions; in agent mode we
   * only ever return matched topics, so this is always `true`.
   */
  matched: Schema.Boolean,
});
export type TestTopicMatch = typeof TestTopicMatch.Type;

export const TestTopicMatchesResult = Schema.Struct({
  testInput: Schema.String,
  /**
   * Mode the tester ran under, mirroring the account's
   * `custom_topic_classifier_mode` setting so the UI can distinguish
   * "no near miss to show" (agent) from "closest candidate didn't match"
   * (embedding).
   */
  mode: CustomTopicClassifierMode,
  /**
   * The runtime classifier's verdict for this test input:
   *
   * - **embedding mode**: array of exactly one entry — the closest topic
   *   in the catalog, with `matched` reflecting whether it crossed the
   *   threshold. Empty only when the catalog itself is empty.
   * - **agent mode**: array of zero or more matched topics (all with
   *   `matched: true`, `distance: null`). Empty when the agent decided
   *   nothing applied.
   */
  matches: Schema.Array(TestTopicMatch),
});
export type TestTopicMatchesResult = typeof TestTopicMatchesResult.Type;

const TestTopicMatchesResponse = Schema.Struct({
  results: Schema.Array(TestTopicMatchesResult),
});

const GetTopicClassifierSettingsResponse = Schema.Struct({
  mode: CustomTopicClassifierMode,
});

const UpdateTopicClassifierSettingsInput = Schema.Struct({
  mode: CustomTopicClassifierMode,
});

export const TopicGroup = HttpApiGroup.make("topic")
  .add(
    endpoint.merchant
      .get("listTopics", "/topics")
      .addSuccess(ListTopicsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("createTopic", "/topics")
      .setPayload(CreateTopicInput)
      .addSuccess(CreateTopicResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateTopic", "/topics/:id")
      .setPath(Schema.Struct({ id: SupportTicketMerchantTopicId }))
      .setPayload(UpdateTopicInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteTopic", "/topics/:id")
      .setPath(Schema.Struct({ id: SupportTicketMerchantTopicId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("testTopicMatches", "/topics/test-matches")
      .setPayload(TestTopicMatchesInput)
      .addSuccess(TestTopicMatchesResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getTopicClassifierSettings", "/topics/classifier-settings")
      .addSuccess(GetTopicClassifierSettingsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateTopicClassifierSettings", "/topics/classifier-settings")
      .setPayload(UpdateTopicClassifierSettingsInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
